import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';

/**
 * 门户页面使用路由级动态导入拆分构建产物，用户只下载当前访问的官网、认证或企业管理模块。
 */
const MarketingPage = () => import('@/views/MarketingPage.vue');
const InformationPage = () => import('@/views/InformationPage.vue');
const AuthPage = () => import('@/views/AuthPage.vue');
const PortalSsoCallbackPage = () => import('@/views/PortalSsoCallbackPage.vue');
const PortalLayout = () => import('@/layout/PortalLayout.vue');
const DashboardPage = () => import('@/views/DashboardPage.vue');
const EnterpriseInfoPage = () => import('@/views/enterprise/EnterpriseInfoPage.vue');
const EnterpriseMembersPage = () => import('@/views/enterprise/EnterpriseMembersPage.vue');
const SubscriptionServicePage = () => import('@/views/finance/SubscriptionServicePage.vue');
const SubscriptionOrderDetailPage = () => import('@/views/finance/SubscriptionOrderDetailPage.vue');
const RechargePlaceholderPage = () => import('@/views/finance/RechargePlaceholderPage.vue');
const RechargeOrdersPage = () => import('@/views/finance/RechargeOrdersPage.vue');
const RechargeOrderDetailPage = () => import('@/views/finance/RechargeOrderDetailPage.vue');
const SubscriptionOrdersPage = () => import('@/views/finance/SubscriptionOrdersPage.vue');
const SubscriptionOrderRecordDetailPage = () => import('@/views/finance/SubscriptionOrderRecordDetailPage.vue');
const WalletTransactionsPage = () => import('@/views/finance/WalletTransactionsPage.vue');
const UserCenterPage = () => import('@/views/user/UserCenterPage.vue');
const HelpCenterPage = () => import('@/views/help/HelpCenterPage.vue');
const NoEnterprisePage = () => import('@/views/enterprise/NoEnterprisePage.vue');

const routes = [
  {
    path: '/',
    name: 'marketing',
    component: MarketingPage,
    meta: { title: 'iDatag 门户' }
  },
  { path: '/privacy', name: 'privacy', component: InformationPage, props: { page: 'privacy' }, meta: { title: '用户隐私声明' } },
  { path: '/terms', name: 'terms', component: InformationPage, props: { page: 'terms' }, meta: { title: '服务使用协议' } },
  { path: '/about', name: 'about', component: InformationPage, props: { page: 'about' }, meta: { title: '关于我们' } },
  {
    path: '/login',
    name: 'login',
    component: AuthPage,
    meta: { guest: true, title: '登录' }
  },
  {
    path: '/sso/callback',
    name: 'portal-sso-callback',
    component: PortalSsoCallbackPage,
    meta: { title: '正在返回门户' }
  },
  {
    path: '/portal',
    component: PortalLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', redirect: '/portal/dashboard' },
      { path: 'dashboard', name: 'dashboard', component: DashboardPage, meta: { title: '仪表盘', requiresEnterprise: true } },
      { path: 'enterprise/info', name: 'enterprise-info', component: EnterpriseInfoPage, meta: { title: '企业信息' } },
      { path: 'enterprise/members', name: 'enterprise-members', component: EnterpriseMembersPage, meta: { title: '企业人员', requiresEnterprise: true } },
      { path: 'finance/subscription', name: 'finance-subscription', component: SubscriptionServicePage, meta: { title: '订阅服务', requiresEnterprise: true } },
      { path: 'finance/subscription/order/:planId', name: 'finance-subscription-order-detail', component: SubscriptionOrderDetailPage, meta: { title: '订阅订单详情', requiresEnterprise: true } },
      { path: 'finance/recharge', name: 'finance-recharge', component: RechargePlaceholderPage, meta: { title: '余额充值', requiresEnterprise: true } },
      { path: 'finance/recharges', name: 'finance-recharges', component: RechargeOrdersPage, meta: { title: '充值订单', requiresEnterprise: true } },
      { path: 'finance/recharges/:id', name: 'finance-recharge-detail', component: RechargeOrderDetailPage, meta: { title: '充值订单详情', requiresEnterprise: true } },
      { path: 'finance/orders', name: 'finance-orders', component: SubscriptionOrdersPage, meta: { title: '订阅订单', requiresEnterprise: true } },
      { path: 'finance/orders/:id', name: 'finance-order-detail', component: SubscriptionOrderRecordDetailPage, meta: { title: '订阅订单详情', requiresEnterprise: true } },
      { path: 'finance/transactions', name: 'finance-transactions', component: WalletTransactionsPage, meta: { title: '资金明细', requiresEnterprise: true } },
      { path: 'enterprise-required', name: 'enterprise-required', component: NoEnterprisePage, meta: { title: '尚未加入企业' } },
      { path: 'user/profile', name: 'user-profile', component: UserCenterPage, meta: { title: '用户中心' } },
      { path: 'help', name: 'help', component: HelpCenterPage, meta: { title: '帮助中心' } }
    ]
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  /**
   * 每次路由切换将主页面滚动到顶部；官网锚点滚动由 MarketingPage 自行处理。
   */
  scrollBehavior() {
    return { top: 0 };
  }
});

/**
 * 门户全局导航守卫依次完成标题设置、登录态分流、企业上下文补载和企业成员资格校验。
 * 已登录用户不能返回访客登录页；需要企业的页面在无企业时跳转占位页，并保存原地址供后续流程使用。
 */
router.beforeEach(async (to, from, next) => {
  document.title = to.meta.title ? `${to.meta.title} - iDatag` : 'iDatag';
  if (to.meta.requiresAuth && !store.getters.isLogin) {
    next('/login');
    return;
  }
  if (to.meta.guest && store.getters.isLogin) {
    next('/portal/dashboard');
    return;
  }
  if (to.meta.requiresAuth && store.getters.isLogin && !store.state.contextLoaded) {
    /* 自动恢复旧会话失败时回登录页，不将后台认证探测作为用户操作警告。 */
    try {
      await store.dispatch('loadContext', { silentAuthFailure: true });
    } catch (error) {
      if (Number(error.code) === 401) { next('/login'); return; }
      next(false);
      return;
    }
  }
  if (to.meta.requiresEnterprise && !store.state.currentEnterprise) {
    next({ name: 'enterprise-required', query: { redirect: to.fullPath } });
    return;
  }
  next();
});

export default router;
